import axios from "axios";
import { tokenFor } from "./session";
import { configureUrl } from "../lib/helper";
import { toast } from "react-toastify";
import i18n from "../i18n/locales/config";
import { closeSession } from "./session";

const isPublicAuthEndpoint = (url) => {
  if (!url) return false;
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/agent-login") ||
    url.includes("/auth/logout") ||
    url.includes("/auth/forgot-password") ||
    url.includes("/auth/reset-password") ||
    url.includes("/verifyEmailOtp") ||
    url.includes("/verifyEmailOtpLogin") ||
    url.includes("/resendEmailOtp")
  );
};

const logoutAndRedirect = (message) => {
  toast.error(message || i18n.t("Errors.SessionExpired"));
  // clear() also took the chosen language and any draft in progress, which
  // belong to the person rather than to the session that just expired.
  closeSession();
  window.location = "/";
};

axios.interceptors.request.use(
  async (config) => {
    let request = config;
    const token = tokenFor("customer");
    request.headers = token
      ? {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          ...request.headers
        }
      : {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...request.headers
        };
    request.url = configureUrl(config.url);
    return request;
  },
  (error) => {
    throw error.response;
  }
);

axios.interceptors.response.use(
  (response) => {
    checkTokenIsValid(response);
    return response;
  },
  (error) => errorHandler(error)
);

/**
 * Too many requests, said once.
 *
 * The API allows sixty a minute per person. When a screen goes past it every
 * call in flight comes back 429 at the same instant, and each service put its
 * own red toast on screen — seven of them, all saying "Too Many Requests",
 * for one thing that happened. This says it once, in words, and keeps quiet
 * about the rest of that burst.
 */
let lastThrottleNotice = 0;

const noticeThrottling = (status) => {
  if (status !== 429) return;
  const now = Date.now();
  if (now - lastThrottleNotice < 10_000) return;
  lastThrottleNotice = now;
  toast.warning(
    i18n.t("Errors.TooMany"),
    { autoClose: 4000 },
  );
};

const checkTokenIsValid = (response) => {
  const url = response?.config?.url;
  if (response?.status === 401 && !isPublicAuthEndpoint(url)) {
    logoutAndRedirect();
  }
};

const errorHandler = async (error) => {
  const url = error?.response?.config?.url;
  if (error?.response?.status == 401 && !isPublicAuthEndpoint(url)) {
    logoutAndRedirect();
  }
  noticeThrottling(error?.response?.status);
  throw error.response;
};

const agentAxios = axios.create({});

export default agentAxios;

agentAxios.interceptors.request.use(
  async (config) => {
    let request = config;
    const token = tokenFor("agent");
    request.headers = token
      ? {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          ...request.headers
        }
      : {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...request.headers
        };
    request.url = configureUrl(config.url);
    return request;
  },
  (error) => {
    throw error.response;
  }
);

agentAxios.interceptors.response.use(
  (response) => {
    checkAgentTokenIsValid(response);
    return response;
  },
  (error) => errorHandlerAgent(error)
);

const checkAgentTokenIsValid = (response) => {
  const url = response?.config?.url;
  if (response?.status == 401 && !isPublicAuthEndpoint(url)) {
    logoutAndRedirect();
  }
};

const errorHandlerAgent = async (error) => {
  const url = error?.response?.config?.url;
  if (error?.response?.status == 401 && !isPublicAuthEndpoint(url)) {
    logoutAndRedirect();
  }
  noticeThrottling(error?.response?.status);
  throw error.response;
};
